import React from 'react';
import { MapPin, Phone, Clock, ExternalLink, Mail } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { businessInfo } from '../data/mock';
import { useLanguage } from '../contexts/LanguageContext';

export const Contact = () => {
  const { t, language } = useLanguage();
  
  return (
    <section id="contact" className="py-20 bg-[#0a0a0a] relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute top-0 left-1/4 w-1/2 h-1/2 bg-gradient-to-br from-[#00D9FF]/10 to-transparent blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-1/2 h-1/2 bg-gradient-to-tl from-[#FF1493]/10 to-transparent blur-3xl" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in">
          <div className="inline-block mb-4">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full glass-effect border border-white/10">
              <MapPin className="w-4 h-4 text-[#00D9FF]" />
              <span className="text-sm font-semibold gradient-text">{t.contact.badge}</span>
            </div>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {t.contact.title} <span className="gradient-text">{t.contact.titleAccent}</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Contact Info Cards */}
          <div className="space-y-6">
            {/* Address Card */}
            <Card className="group bg-[#121212] border-white/10 hover:border-[#00D9FF]/50 transition-all duration-500 hover:shadow-2xl hover:shadow-[#00D9FF]/20 animate-slide-in-left">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00D9FF]/20 to-[#00D9FF]/5 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <MapPin className="w-7 h-7 text-[#00D9FF]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-semibold text-lg mb-2">{t.contact.address}</h3>
                    <p className="text-gray-400 mb-3">{businessInfo.address}</p>
                    <a
                      href={businessInfo.googleMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        size="sm"
                        className="bg-gradient-to-r from-[#00D9FF] to-[#5B7FFF] text-white hover:shadow-lg hover:shadow-[#00D9FF]/30 transition-all duration-300 border-0 hover:scale-105"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        {t.contact.directions}
                      </Button>
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Phone Card */}
            <Card className="group bg-[#121212] border-white/10 hover:border-[#FF1493]/50 transition-all duration-500 hover:shadow-2xl hover:shadow-[#FF1493]/20 animate-slide-in-left" style={{ animationDelay: '0.1s' }}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#FF1493]/20 to-[#FF1493]/5 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <Phone className="w-7 h-7 text-[#FF1493]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-semibold text-lg mb-2">{t.contact.phone}</h3>
                    <p className="text-gray-400 mb-3 text-xl font-semibold">{businessInfo.phone}</p>
                    <a href="tel:4389221776">
                      <Button
                        size="sm"
                        className="bg-gradient-to-r from-[#FF1493] to-[#E91E63] text-white hover:shadow-lg hover:shadow-[#FF1493]/30 transition-all duration-300 border-0 hover:scale-105"
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        {t.contact.call}
                      </Button>
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Email Card */}
            <Card className="group bg-[#121212] border-white/10 hover:border-[#00D9FF]/50 transition-all duration-500 hover:shadow-2xl hover:shadow-[#00D9FF]/20 animate-slide-in-left" style={{ animationDelay: '0.15s' }}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00D9FF]/20 to-[#00D9FF]/5 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <Mail className="w-7 h-7 text-[#00D9FF]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-semibold text-lg mb-2">Email</h3>
                    <p className="text-gray-400 mb-3 text-lg">{businessInfo.email}</p>
                    <a href={`mailto:${businessInfo.email}`}>
                      <Button
                        size="sm"
                        className="bg-gradient-to-r from-[#00D9FF] to-[#5B7FFF] text-white hover:shadow-lg hover:shadow-[#00D9FF]/30 transition-all duration-300 border-0 hover:scale-105"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        {t.contact.sendEmail}
                      </Button>
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Hours Card */}
            <Card className="group bg-[#121212] border-white/10 hover:border-[#5B7FFF]/50 transition-all duration-500 hover:shadow-2xl hover:shadow-[#5B7FFF]/20 animate-slide-in-left" style={{ animationDelay: '0.2s' }}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#5B7FFF]/20 to-[#5B7FFF]/5 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <Clock className="w-7 h-7 text-[#5B7FFF]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-semibold text-lg mb-4">{t.contact.hours}</h3>
                    <div className="space-y-3">
                      {businessInfo.hours.map((schedule, index) => (
                        <div key={index} className="flex justify-between items-center group/item">
                          <span className="text-gray-400 group-hover/item:text-white transition-colors">{language === 'en' ? schedule.dayEn : schedule.day}</span>
                          <span
                            className={`font-medium px-3 py-1 rounded-full text-sm ${
                              schedule.open
                                ? 'bg-gradient-to-r from-[#00D9FF]/20 to-[#5B7FFF]/20 text-[#00D9FF]'
                                : 'bg-gray-800 text-gray-500'
                            }`}
                          >
                            {language === 'en' ? schedule.hoursEn : schedule.hours}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Map with gradient border */}
          <div className="relative h-[600px] lg:h-full min-h-[500px] rounded-2xl overflow-hidden animate-slide-in-right glass-effect border border-white/10">
            <div className="absolute inset-0 bg-gradient-to-br from-[#00D9FF]/10 to-[#FF1493]/10" />
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2798.8574843866757!2d-73.60116!3d45.44785!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cc9106c18b7e3fd%3A0x8e9a1f1f1f1f1f1f!2s2911%20Rue%20Allard%2C%20Montr%C3%A9al%2C%20QC%20H4E%202M4!5e0!3m2!1sen!2sca!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="DNA Barbershop Location"
              className="relative z-10"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
